import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, LogIn, LogOut, MessageSquare, Zap, Unplug } from 'lucide-react';
import { serverStore } from '../../../stores/server.store';
import { parseActivity, type ActivityKind } from '../../../utils/activity-parser';
import { cn } from '@/utils/cn';

const KIND_STYLE: Record<ActivityKind, { icon: typeof Zap; cls: string }> = {
  join: { icon: LogIn, cls: 'text-online bg-online/10' },
  leave: { icon: LogOut, cls: 'text-fg-muted bg-white/[0.06]' },
  chat: { icon: MessageSquare, cls: 'text-fg bg-white/[0.06]' },
  ready: { icon: Zap, cls: 'text-ember bg-ember/10' },
  lost: { icon: Unplug, cls: 'text-warn bg-warn/10' },
};

type View = 'activity' | 'log';
const VIEW_KEY = 'gm.overview.activityView';

const readView = (): View => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'log' ? 'log' : 'activity';
  } catch {
    return 'activity';
  }
};

/**
 * Lesbare Aktivitäts-Liste (Joins, Chat, bereit) – umschaltbar auf die letzten
 * Rohausgaben. Alles Weitere steht vollständig in der Konsole.
 * `fill`: Karte füllt die Höhe ihrer Spalte (Übersicht neben den hohen Quick Actions).
 */
export const ActivityCard = observer(({ fill = false }: { fill?: boolean }) => {
  const { activeServer } = serverStore;
  const navigate = useNavigate();
  const [view, setView] = useState<View>(readView);
  const logRef = useRef<HTMLDivElement>(null);
  const lines = activeServer?.logs || [];

  useEffect(() => {
    if (view === 'log' && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [view, lines.length]);

  if (!activeServer) return null;
  const online = activeServer.status === 'online';
  const events = parseActivity(lines, fill ? 10 : 6);
  const tail = lines.slice(-40);

  const pick = (v: View) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* egal */ }
  };

  return (
    <div className={cn('bg-surface border border-line rounded-2xl overflow-hidden', fill && 'flex flex-col flex-1 min-h-[22rem]')}>
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-line shrink-0">
        <div className="flex items-center gap-1 p-0.5 rounded-lg bg-black/25 border border-line" role="tablist">
          {([['activity', 'Aktivität'], ['log', 'Log']] as const).map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={view === id}
              onClick={() => pick(id)}
              className={cn(
                'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                view === id ? 'bg-white/[0.09] text-fg' : 'text-fg-muted hover:text-fg',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="hidden sm:block flex-1 text-xs text-fg-muted truncate">
          {view === 'activity' ? 'Joins, Chat und Server-Ereignisse' : 'Die letzten Ausgaben des Servers'}
        </span>
        <button
          onClick={() => navigate(`/server/${activeServer.id}/console`)}
          className="text-xs font-medium text-ember hover:text-ember-2 transition-colors inline-flex items-center gap-1 shrink-0"
        >
          Zur Konsole <ArrowRight size={13} />
        </button>
      </div>

      {view === 'log' ? (
        <div
          ref={logRef}
          // Im Füllmodus zählt der Log-Inhalt nicht zur Spaltenhöhe (basis-0) – die Karte wächst nur bis zur Nachbarspalte.
          className={cn('bg-ink font-mono text-xs p-4 overflow-y-auto custom-scrollbar space-y-0.5', fill ? 'flex-1 basis-0 min-h-0' : 'h-72')}
        >
          {tail.length === 0 ? (
            <div className="text-fg-muted/60 py-6 text-center">
              Noch keine Logs — {online ? 'warte auf Ausgaben…' : 'starte den Server, um Logs zu sehen.'}
            </div>
          ) : (
            tail.map((log, i) => (
              <div key={i} className="text-fg-muted whitespace-pre-wrap break-all hover:bg-white/[0.04] hover:text-fg px-2 py-0.5 rounded transition-colors">
                {log}
              </div>
            ))
          )}
        </div>
      ) : events.length === 0 ? (
        <div className="py-8 text-center text-sm text-fg-muted">
          Noch nichts los — Joins und Chat erscheinen hier, sobald jemand spielt.
        </div>
      ) : (
        <div className="px-3 pt-1.5 pb-2.5">
          {events.map((e, i) => {
            const { icon: Icon, cls } = KIND_STYLE[e.kind];
            return (
              <div
                key={`${e.time}-${i}`}
                className={cn('grid grid-cols-[60px_30px_minmax(0,1fr)] items-center px-2 py-2.5', i < events.length - 1 && 'border-b border-white/[0.05]')}
              >
                <span className="font-mono text-xs text-fg-muted">{e.time}</span>
                <span className={cn('w-[22px] h-[22px] rounded-full flex items-center justify-center', cls)}>
                  <Icon size={12} />
                </span>
                <span className="text-[13.5px] text-fg truncate">
                  {e.kind === 'chat' ? (
                    <><strong className="font-semibold">{e.name}</strong> <span className="text-[#B4B4C2]">„{e.text}“</span></>
                  ) : e.name ? (
                    <><strong className="font-semibold">{e.name}</strong> {e.text}</>
                  ) : (
                    e.text
                  )}
                  {e.detail && <span className="text-fg-muted"> · {e.detail}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
